import { Box, IconButton, InputAdornment, Menu, MenuItem, Avatar, Divider, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowBack, MoreVert, Search } from '@mui/icons-material';
import logo from '../images/logo.svg';
import logo2 from '../images/Logo 2 (SVG).svg'
import { cardBlur, colors, radius, typography } from '../theme';

// ─── NavBar ───────────────────────────────────────────────────────────────────
// Two modes:
//   showSearch=true  → shows title + search bar (UserDashboard)
//   showSearch=false → shows back button + page title + avatar (inner pages)
// ─────────────────────────────────────────────────────────────────────────────
const NavBar = ({
    showSearch = false,
    searchValue = '',
    onSearch = null,
    placeholder = 'Search shops...',
    title = '',
    showBack = false,
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [userName, setUserName] = useState('');
    const [anchorElUser, setAnchorElUser] = useState(null);

    useEffect(() => {
        if (!user?.uid) return;
        getDoc(doc(db, 'users', user.uid)).then(snap => {
            if (snap.exists()) setUserName(snap.data().name ?? '');
        });
    }, [user?.uid]);

    const initials = userName
        ? userName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
        : user?.email?.[0]?.toUpperCase() ?? '?';

    const handleSettingsPage = async (setting) => {
        setAnchorElUser(null);
        if (setting === 'Profile') navigate('/user/profile');
        if (setting === 'Bookings') navigate('/user/bookings');
        if (setting === 'Dashboard') navigate('/user/dashboard');
        if (setting === 'Logout') { await signOut(auth); navigate('/'); }
    };

    const menuPaperSx = {
        bgcolor: colors.bgCard, border: `1px solid ${colors.border}`,
        borderRadius: radius.md, mt: 0.5, minWidth: 160,
    };

    if (showSearch) {
        // ── Dashboard mode: title centred + search bar below ────────────────
        return (
            <Box sx={{ px: 2, pt: 3, pb: 1.5, background: colors.bgGradient }}>
                {/* Title row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: colors.textPrimary, p: 0.5 }}>
                        <ArrowBack sx={{ fontSize: 20 }} />
                    </IconButton>

                    <img src={logo2} alt="DigitalQ" style={{ width: 55, height: 'auto', imageRendering: 'crisp-edges' }} />


                    <Tooltip title="Account">
                        <IconButton onClick={e => setAnchorElUser(e.currentTarget)} sx={{ p: 0 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: colors.primary, color: '#322920', fontSize: 12, fontWeight: 700, border: `2px solid ${colors.border}` }}>
                                {initials}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: colors.primary, textAlign: 'center' }}>
                    {title}
                </Typography>
                {/* Search bar */}
                <TextField
                    fullWidth size="small"
                    placeholder={placeholder}
                    value={searchValue}
                    onChange={e => onSearch?.(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ fontSize: 18, color: '#322920' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            background: colors.primary,
                            backdropFilter: cardBlur,
                            WebkitBackdropFilter: cardBlur,
                            borderRadius: 999,
                            fontSize: 13,
                            '& fieldset': { borderColor: 'transparent' },
                            '&:hover fieldset': { borderColor: `${colors.primary}80` },
                            '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 1.5 },
                        },
                        '& input': { color: '#322920', fontWeight: 500 },
                        '& input::placeholder': { color: '#32292088', opacity: 1 },
                    }}
                />

                {/* Avatar menu */}
                <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={() => setAnchorElUser(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ mt: 1, '& .MuiPaper-root': menuPaperSx }}>
                    {['Profile', 'Bookings', 'Logout'].map(s => (
                        <Box key={s}>
                            {s === 'Logout' && <Divider sx={{ borderColor: colors.border, my: 0.5 }} />}
                            <MenuItem onClick={() => handleSettingsPage(s)}
                                sx={{ color: s === 'Logout' ? colors.error : colors.textPrimary, fontSize: 14, '&:hover': { bgcolor: colors.bgDeep } }}>
                                {s}
                            </MenuItem>
                        </Box>
                    ))}
                </Menu>
            </Box>
        );
    }

    // ── Inner page mode: back button + title + avatar ────────────────────────
    return (
        <Box sx={{ px: 2, pt: 3,  background: colors.bgGradient }}>
            {/* Title row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: colors.textPrimary, p: 0.5 }}>
                    <ArrowBack sx={{ fontSize: 20 }} />
                </IconButton>

                <img src={logo2} alt="DigitalQ" style={{ width: 55, height: 'auto', imageRendering: 'crisp-edges' }} />


                <Tooltip title="Account">
                    <IconButton onClick={e => setAnchorElUser(e.currentTarget)} sx={{ p: 0 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: colors.primary, color: '#322920', fontSize: 12, fontWeight: 700, border: `2px solid ${colors.border}` }}>
                            {initials}
                        </Avatar>
                    </IconButton>
                </Tooltip>
            </Box>
            <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: colors.primary, textAlign: 'center' }}>
                {title}
            </Typography>


            {/* Avatar menu */}
            <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={() => setAnchorElUser(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 1, '& .MuiPaper-root': menuPaperSx }}>
                {['Profile', 'Bookings', 'Logout'].map(s => (
                    <Box key={s}>
                        {s === 'Logout' && <Divider sx={{ borderColor: colors.border, my: 0.5 }} />}
                        <MenuItem onClick={() => handleSettingsPage(s)}
                            sx={{ color: s === 'Logout' ? colors.error : colors.textPrimary, fontSize: 14, '&:hover': { bgcolor: colors.bgDeep } }}>
                            {s}
                        </MenuItem>
                    </Box>
                ))}
            </Menu>
        </Box>
    );
};

export default NavBar;